import { useState, useCallback } from 'react'
import { ConversationMessage, DisplayMessage, ContentBlock } from '../types/chat'
import { sendChatMessage, createDocumentMessage } from '../services/chatService'
import {
  loadMessages,
  saveMessages,
  clearMessages,
  saveExtractedCV,
  loadExtractedCV,
  clearExtractedCV,
} from '../services/memoryStorage'
import { ParsedDocument } from '../types/document'

interface UseChatOptions {
  memory: string
  currentLatex: string
  pageCount: number | null
  onMemoryUpdate: (memory: string | null) => void
  onLatexUpdate: (latex: string) => void
}

interface UseChatReturn {
  messages: DisplayMessage[]
  sendMessage: (content: string) => Promise<void>
  sendDocument: (doc: ParsedDocument, filename: string) => Promise<void>
  isLoading: boolean
  error: string | null
  resetChat: () => void
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Extract display text from message content (handles both string and ContentBlock[])
 */
function getDisplayContent(content: string | ContentBlock[]): string {
  if (typeof content === 'string') {
    return content
  }
  // For ContentBlock arrays, extract text from text blocks
  const textBlocks = content.filter((block): block is { type: 'text'; text: string } => block.type === 'text')
  return textBlocks.map((block) => block.text).join('\n')
}

function toDisplayMessages(messages: ConversationMessage[]): DisplayMessage[] {
  return messages.map((m) => ({
    type: m.role === 'assistant' ? 'ai' : 'user',
    content: getDisplayContent(m.content),
    time: '', // We don't store times for persisted messages
  }))
}

function toConversationMessages(messages: DisplayMessage[]): ConversationMessage[] {
  return messages.map((m) => ({
    role: m.type === 'ai' ? 'assistant' : 'user',
    content: m.content,
  }))
}

const INITIAL_MESSAGE: DisplayMessage = {
  type: 'ai',
  content:
    "Hi! I'm your resume coach. Let's build something great together. What kind of role are you targeting?",
  time: formatTime(new Date()),
}

export function useChat({
  memory,
  currentLatex,
  pageCount,
  onMemoryUpdate,
  onLatexUpdate,
}: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<DisplayMessage[]>(() => {
    const stored = loadMessages()
    if (stored.length > 0) {
      return toDisplayMessages(stored)
    }
    return [INITIAL_MESSAGE]
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendMessage = useCallback(
    async (content: string) => {
      // Add user message to display
      const userMessage: DisplayMessage = {
        type: 'user',
        content,
        time: formatTime(new Date()),
      }

      const updatedMessages = [...messages, userMessage]
      setMessages(updatedMessages)
      setIsLoading(true)
      setError(null)

      try {
        // Convert to API format
        const apiMessages = toConversationMessages(updatedMessages)

        // Include extracted CV in memory if available
        const extractedCV = loadExtractedCV()
        const enhancedMemory = extractedCV
          ? `${memory}\n\n[User's uploaded CV - ${extractedCV.filename}]\n${extractedCV.text}`
          : memory

        // Send to API
        const response = await sendChatMessage(apiMessages, enhancedMemory, currentLatex, pageCount)

        // Add assistant response
        const assistantMessage: DisplayMessage = {
          type: 'ai',
          content: response.conversationResponse,
          time: formatTime(new Date()),
        }

        const finalMessages = [...updatedMessages, assistantMessage]
        setMessages(finalMessages)

        // Persist conversation
        saveMessages(toConversationMessages(finalMessages))

        // Apply updates
        if (response.updatedMemory !== null) {
          onMemoryUpdate(response.updatedMemory)
        }
        if (response.latexCode !== null) {
          onLatexUpdate(response.latexCode)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to send message')
      } finally {
        setIsLoading(false)
      }
    },
    [messages, memory, currentLatex, pageCount, onMemoryUpdate, onLatexUpdate]
  )

  const sendDocument = useCallback(
    async (doc: ParsedDocument, filename: string) => {
      if (doc.type === 'text') {
        // For text documents, send the extracted content as a message
        const content = `Here's my CV from "${filename}":\n\n${doc.content}`
        await sendMessage(content)
      } else if (doc.type === 'pdf') {
        // Save extracted text for future turns
        saveExtractedCV(filename, doc.extractedText)

        // For PDF documents, create a document message with content blocks
        const documentMessage = createDocumentMessage(doc.data, filename)

        // Create display message for the user to see
        const displayText = `Here is my CV from "${filename}". Please analyze it and help me improve it.`
        const userDisplayMessage: DisplayMessage = {
          type: 'user',
          content: displayText,
          time: formatTime(new Date()),
        }

        const updatedMessages = [...messages, userDisplayMessage]
        setMessages(updatedMessages)
        setIsLoading(true)
        setError(null)

        try {
          // Build API messages: previous messages + document message
          const previousApiMessages = toConversationMessages(messages)
          const apiMessages = [...previousApiMessages, documentMessage]

          // Send to API
          const response = await sendChatMessage(apiMessages, memory, currentLatex, pageCount)

          // Add assistant response
          const assistantMessage: DisplayMessage = {
            type: 'ai',
            content: response.conversationResponse,
            time: formatTime(new Date()),
          }

          const finalMessages = [...updatedMessages, assistantMessage]
          setMessages(finalMessages)

          // Persist conversation (note: document blocks won't be persisted, only text)
          saveMessages(toConversationMessages(finalMessages))

          // Apply updates
          if (response.updatedMemory !== null) {
            onMemoryUpdate(response.updatedMemory)
          }
          if (response.latexCode !== null) {
            onLatexUpdate(response.latexCode)
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Failed to send document')
        } finally {
          setIsLoading(false)
        }
      }
    },
    [messages, memory, currentLatex, pageCount, onMemoryUpdate, onLatexUpdate, sendMessage]
  )

  const resetChat = useCallback(() => {
    clearMessages()
    clearExtractedCV()
    setMessages([INITIAL_MESSAGE])
    setError(null)
  }, [])

  return {
    messages,
    sendMessage,
    sendDocument,
    isLoading,
    error,
    resetChat,
  }
}
